/* (c) Quick Pro USA (quickprousa.com) - proprietary theme code. All rights reserved. */
/*
 * qp-rime-close-map — compact dealer map for the RIME AERO closing band.
 * Mirrors the tile layer, attribution, zoom control and divIcon pins of
 * assets/store-locator-map.js (keep the two in sync; a gate test compares the
 * tile URL). No search, list or geolocation — the locator page owns those.
 *
 * Lazy: Leaflet (same unpkg 1.9.4 build as sections/store-locator-map.liquid,
 * URLs passed in data attributes) and stores-data.json load only when the
 * panel nears the viewport. Until the map is ready the static fallback link
 * stays visible, so a failure never leaves an empty box.
 */
(function () {
  if (window.QpRimeCloseMap) {
    window.QpRimeCloseMap.boot(document);
    return;
  }

  var leafletPromise = null;

  function loadCss(href) {
    return new Promise(function (resolve) {
      var existing = document.querySelector('link[href="' + href + '"]');
      if (existing) return resolve();
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = function () { resolve(); };
      link.onerror = function () { resolve(); };
      document.head.appendChild(link);
    });
  }

  function loadJs(src) {
    return new Promise(function (resolve, reject) {
      if (typeof window.L !== 'undefined') return resolve();
      var existing = document.querySelector('script[src="' + src + '"]');
      var script = existing || document.createElement('script');
      script.addEventListener('load', function () { resolve(); });
      script.addEventListener('error', function () { reject(new Error('leaflet failed')); });
      if (!existing) {
        script.src = src;
        script.async = true;
        document.head.appendChild(script);
      }
    });
  }

  function loadLeaflet(jsUrl, cssUrl) {
    if (!leafletPromise) {
      leafletPromise = Promise.all([loadCss(cssUrl), loadJs(jsUrl)]).catch(function (err) {
        leafletPromise = null;
        throw err;
      });
    }
    return leafletPromise;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* Same shape/size/anchor as StoreLocatorMap.prototype.pinIcon; colours come
   * from qp-rime-close.css (tokens) instead of inline fills. */
  function pinIcon(type) {
    var shape = type === 'showroom'
      ? '<rect x="4" y="4" width="16" height="16" transform="rotate(45 12 12)"/>'
      : '<circle cx="12" cy="12" r="6"/>';
    return L.divIcon({
      html: '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + shape + '</svg>',
      className: 'qp-rime-close__pin qp-rime-close__pin--' + type,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  }

  function RimeMap(el) {
    this.el = el;
    this.canvas = el.querySelector('[data-map-canvas]');
    this.fallback = el.querySelector('[data-map-fallback]');
    this.map = null;
    this.observer = null;
    this.destroyed = false;
    this.observe();
  }

  RimeMap.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) return this.start();
    this.observer = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) {
        self.observer.disconnect();
        self.observer = null;
        self.start();
      }
    }, { rootMargin: '300px 0px' });
    this.observer.observe(this.el);
  };

  RimeMap.prototype.start = function () {
    var self = this;
    var ds = this.el.dataset;
    Promise.all([
      loadLeaflet(ds.leafletJs, ds.leafletCss),
      fetch(ds.storesUrl).then(function (res) {
        if (!res.ok) throw new Error('stores ' + res.status);
        return res.json();
      })
    ]).then(function (results) {
      if (!self.destroyed) self.render(results[1]);
    }).catch(function () {
      /* fallback link stays visible */
    });
  };

  RimeMap.prototype.render = function (stores) {
    if (typeof L === 'undefined' || !this.canvas || this.map) return;
    var ds = this.el.dataset;
    var pins = (Array.isArray(stores) ? stores : []).filter(function (s) {
      return s && typeof s.lat === 'number' && typeof s.lng === 'number';
    });

    this.canvas.hidden = false;
    var desktop = window.matchMedia('(min-width: 1024px)').matches;

    // Options mirror StoreLocatorMap.prototype.initMap.
    this.map = L.map(this.canvas, {
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: false,
      dragging: desktop
    }).setView(
      [parseFloat(ds.defaultLat || '39.8283'), parseFloat(ds.defaultLng || '-98.5795')],
      parseInt(ds.defaultZoom || '4', 10)
    );

    L.control.zoom({ position: 'topright' }).addTo(this.map);
    L.control.attribution({ prefix: false, position: 'bottomleft' }).addTo(this.map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map);

    var map = this.map;
    pins.forEach(function (loc) {
      var type = loc.type === 'showroom' ? 'showroom' : 'dealer';
      var place = [loc.city, loc.state].filter(function (v) { return v && String(v).trim(); }).join(', ');
      var marker = L.marker([loc.lat, loc.lng], {
        icon: pinIcon(type),
        title: loc.name,
        keyboard: false // 65 pins would be 65 tab stops; the locator link is the keyboard path
      }).addTo(map);
      marker.bindTooltip(
        '<strong>' + escapeHtml(loc.name) + '</strong>' + (place ? '<br>' + escapeHtml(place) : ''),
        { direction: 'top', className: 'qp-rime-close__tooltip' }
      );
    });

    if (pins.length) {
      map.fitBounds(L.latLngBounds(pins.map(function (s) { return [s.lat, s.lng]; })), { padding: [24, 24] });
    }

    this.el.classList.add('is-ready');
    if (this.fallback) this.fallback.hidden = true;
  };

  RimeMap.prototype.destroy = function () {
    this.destroyed = true;
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    if (this.map) this.map.remove();
    this.map = null;
    delete this.el.__qpRimeMap;
  };

  function boot(scope) {
    (scope || document).querySelectorAll('[data-qp-rime-map]').forEach(function (el) {
      if (!el.__qpRimeMap) el.__qpRimeMap = new RimeMap(el);
    });
  }

  function teardown(scope) {
    scope.querySelectorAll('[data-qp-rime-map]').forEach(function (el) {
      if (el.__qpRimeMap) el.__qpRimeMap.destroy();
    });
  }

  window.QpRimeCloseMap = { boot: boot };

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
})();
